import React, { useEffect, useState, useRef } from 'react';
import { Grid, Card, CardMedia, Pagination, Box, CircularProgress, Typography } from '@mui/material';
import { getPrintFiles } from '../../api';

interface PrintFile {
    printFileId: number;
    url: string;
    mockupUrl: string | null;
    printFileReadyUrl: string;
    mockupUrlInternal: string | null;
    printFileShortUrl: string;
    createdAt: string;
}

interface PrintFilesProps {
    clientId: string;
}

const PrintFiles: React.FC<PrintFilesProps> = ({ clientId }) => {
    const [images, setImages] = useState<PrintFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [perPage, setPerPage] = useState(500);
    const scrollRef = useRef<HTMLDivElement>(null);

    const fetchImages = async (pageNumber: number) => {
        setLoading(true);
        try {
            const data = await getPrintFiles({ clientId, page: pageNumber, perPage });
            setImages(data.result);
            // Calculate total pages based on totalCount and perPage
            setTotalPages(Math.ceil(data.totalCount / data.perPage));
        } catch (error) {
            console.error("Failed to fetch print files", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clientId) {
            fetchImages(page);
        }
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [clientId, page]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <Box sx={{ width: '100%', padding: 2 }}>
            {loading && images.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {images.length === 0 ? (
                        <Typography>No images found.</Typography>
                    ) : (
                        <Grid container spacing={2} sx={{ maxHeight: '80vh', overflow: 'auto' }} ref={scrollRef}>
                            {images.map((image) => (
                                <Grid item xs={6} sm={4} md={3} lg={2} key={image.printFileId}>
                                    <Card>
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={image.printFileReadyUrl}
                                            alt={`Print File ${image.printFileId}`}
                                            sx={{ objectFit: 'contain', padding: 1 }}
                                        />
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                        <Pagination 
                            count={totalPages} 
                            page={page} 
                            onChange={handlePageChange} 
                            color="primary" 
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default PrintFiles;
